import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

import { Button } from './ui';
import { t } from './i18n';

/**
 * A dialog that behaves like one.
 *
 * Built on <dialog showModal>, which the browser already gets right: focus goes inside,
 * the rest of the page becomes inert, Escape closes, and the backdrop is a real one. The
 * hand-rolled version this replaces had none of that — Tab walked straight out of it into
 * the page behind, and it guarded the only irreversible action in the console.
 */
export function Modal({
  open, title, onClose, children, footer, wide,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const opener = useRef<Element | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) {
      opener.current = document.activeElement;
      el.showModal();
    } else if (!open && el.open) {
      el.close();
      // Focus goes back where it came from, so keyboard users are not dropped at the top
      // of the document every time they cancel.
      (opener.current as HTMLElement | null)?.focus?.();
    }
  }, [open]);

  if (!open) return null;
  return (
    <dialog
      ref={ref}
      // Escape fires "cancel" before "close"; routing both through onClose keeps React the
      // one thing that decides whether the dialog is open.
      onCancel={(e) => { e.preventDefault(); onClose(); }}
      onClose={onClose}
      aria-labelledby="modal-title"
      className={`m-auto w-[calc(100vw-2rem)] rounded-[6px] border border-[var(--color-line)]
        bg-[var(--color-surface)] p-0 text-[var(--color-ink)] backdrop:bg-[rgba(25,23,19,.45)]
        ${wide ? 'max-w-2xl' : 'max-w-md'}`}
    >
      <div className="border-b border-[var(--color-line)] px-5 py-3">
        <h2 id="modal-title" className="text-[15px] font-semibold">{title}</h2>
      </div>
      <div className="max-h-[70vh] overflow-y-auto px-5 py-4">{children}</div>
      {footer && (
        <div className="flex flex-wrap justify-end gap-2 border-t border-[var(--color-line)] px-5 py-3">
          {footer}
        </div>
      )}
    </dialog>
  );
}

interface Ask {
  title: string;
  body: ReactNode;
  confirmLabel: string;
  danger?: boolean;
  onConfirm: () => void;
}

/**
 * Confirmation for actions that cannot be undone.
 *
 * Deliberately not used for everything: a dialog in front of a reversible action trains
 * people to click through dialogs, which is how the irreversible one gets confirmed
 * without being read. Reach for this only where there is no undo — revoking a key that
 * lives in someone's CI, removing a colleague, deleting a block rule that is the only
 * thing stopping a tracker.
 */
export function useConfirm(): [ReactNode, (ask: Ask) => void] {
  const [ask, setAsk] = useState<Ask | null>(null);
  const close = useCallback(() => setAsk(null), []);

  const node = (
    <Modal
      open={ask !== null}
      title={ask?.title ?? ''}
      onClose={close}
      footer={
        <>
          <Button variant="ghost" onClick={close}>{t('Abbrechen')}</Button>
          <Button
            variant={ask?.danger ? 'danger' : 'primary'}
            onClick={() => { ask?.onConfirm(); close(); }}
          >
            {ask?.confirmLabel ?? t('Bestätigen')}
          </Button>
        </>
      }
    >
      <div className="text-sm leading-relaxed">{ask?.body}</div>
    </Modal>
  );

  return [node, setAsk];
}
